/**
 * Session Timeout Warning Component
 *
 * SECURITY: Displays a warning when the user's session is about to expire.
 * Gives users the option to extend their session or logout.
 *
 * This component should be included in the main App layout for authenticated routes.
 */
import { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../authProvider.mjs';
import { apiService } from '../middleware/refreshToken.mjs';

// Session timeout configuration (in milliseconds)
const WARNING_TIME_BEFORE_EXPIRY = 2 * 60 * 1000; // Show warning 2 minutes before expiry
const SESSION_DURATION = 15 * 60 * 1000; // 15 minute session (should match backend)
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

const SessionTimeoutWarning = () => {
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const { isAuthenticated, logout } = useContext(AuthContext);

    const extendSession = useCallback(async () => {
        try {
            await apiService.manualRefresh();
            setShowWarning(false);
        } catch (error) {
            // If refresh fails, force logout
            logout();
        }
    }, [logout]);

    const handleLogout = useCallback(() => {
        setShowWarning(false);
        logout();
    }, [logout]);

    useEffect(() => {
        if (!isAuthenticated) {
            setShowWarning(false);
            return;
        }

        let warningTimeout;
        let countdownInterval;
        let lastActivity = Date.now();

        const scheduleWarning = () => {
            // Clear existing timers
            if (warningTimeout) clearTimeout(warningTimeout);
            if (countdownInterval) clearInterval(countdownInterval);

            const timeUntilWarning = SESSION_DURATION - WARNING_TIME_BEFORE_EXPIRY;

            warningTimeout = setTimeout(() => {
                const timeSinceActivity = Date.now() - lastActivity;

                // Only show warning if user has been inactive
                if (timeSinceActivity >= timeUntilWarning) {
                    setShowWarning(true);
                    setTimeRemaining(Math.floor(WARNING_TIME_BEFORE_EXPIRY / 1000));

                    // Start countdown
                    countdownInterval = setInterval(() => {
                        setTimeRemaining(prev => {
                            if (prev <= 1) {
                                clearInterval(countdownInterval);
                                logout();
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);
                } else {
                    // User was active, reschedule
                    scheduleWarning();
                }
            }, timeUntilWarning);
        };

        const handleActivity = () => {
            lastActivity = Date.now();
            // Only reschedule when the warning is not already visible.
            // Reading showWarning here via closure is intentional - we do NOT
            // want showWarning in the effect deps (that would reset lastActivity
            // and restart timers every time the warning appears, breaking logout).
            setShowWarning(current => {
                if (!current) scheduleWarning();
                return current;
            });
        };

        // Set up activity listeners
        ACTIVITY_EVENTS.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });

        // Initial schedule
        scheduleWarning();

        return () => {
            if (warningTimeout) clearTimeout(warningTimeout);
            if (countdownInterval) clearInterval(countdownInterval);
            ACTIVITY_EVENTS.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, logout]);

    if (!showWarning || !isAuthenticated) {
        return null;
    }

    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-timeout-title"
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-3 sm:mx-4 p-5 sm:p-6 max-h-[calc(100dvh-1.5rem)] overflow-y-auto sm:max-h-none sm:overflow-visible">
                <h2 id="session-timeout-title" className="text-xl font-semibold text-gray-800 mb-4">
                    Session Expiring Soon
                </h2>
                <p className="text-gray-600 mb-2">
                    Your session will expire in{' '}
                    <strong className="text-stanford-red">
                        {minutes > 0 ? `${minutes} minute${minutes !== 1 ? 's' : ''} ` : ''}
                        {seconds} second{seconds !== 1 ? 's' : ''}
                    </strong>
                </p>
                <p className="text-gray-600 mb-6">Would you like to continue your session?</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={extendSession}
                        className="sm:flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors font-medium"
                        autoFocus
                    >
                        Continue Session
                    </button>
                    <button
                        onClick={handleLogout}
                        className="sm:flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                        Logout Now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionTimeoutWarning;
